/** Select an entity dialog */

import { Button, Dialog, DialogActions, DialogContent } from '@tunasong/ui-lib'

import { graphHooks } from '@tunasong/graph-lib/react'
import type { FilterFunc } from '@tunasong/models'
import { isTopLevelEntity } from '@tunasong/models'
import type { Entity, Persisted } from '@tunasong/schemas'
import { EntityTree, Typography } from '@tunasong/ui-lib'
import type { FC, MouseEvent } from 'react'
import { useCallback, useState } from 'react'

interface SelectEntityDialogProps {
  open: boolean
  excludeIds?: string[]
  entities?: Persisted<Entity>[]
  filter?: FilterFunc
  title?: string
  /**
   * Opt-in only: renders the tree's "Top" item so a caller can let the user
   * pick root (the dummy `{ id: 'ROOT' }` entity below). Defaults to false so
   * every other caller of this shared dialog keeps its current behaviour.
   */
  includeRoot?: boolean
  /**
   * Shown above the tree when set. "Top" means something different to every
   * caller of this shared dialog (the Inbox for a move destination, "no
   * filter" for a scope filter, plain root elsewhere) — this is where a
   * caller spells that meaning out instead of leaving the user to guess.
   */
  description?: string
  /**
   * Confirm button label; "OK" by default. A pick that starts a job — an export, a long
   * import — should say what it starts, so the user is not agreeing to an unnamed action.
   */
  confirmLabel?: string
  /** Why an entity can't be chosen; it stays listed, disabled, with the reason (GH #2450) */
  disabledReason?: (entity: Persisted<Entity>) => string | undefined
  onClose(selected?: Persisted<Entity>): void
}

export const EntitySelectDialog: FC<SelectEntityDialogProps> = props => {
  const {
    open,
    filter = isTopLevelEntity,
    title = 'Select an entity',
    excludeIds = [],
    entities,
    includeRoot = false,
    description,
    confirmLabel = 'OK',
    disabledReason,
    onClose,
  } = props
  const [selected, setSelected] = useState<Persisted<Entity>>()
  const { entities: allEntities } = graphHooks.useEntities({ root: true })

  const handleCancel = useCallback(() => {
    setSelected(undefined)
    onClose()
  }, [onClose])

  const handleOK = useCallback(() => {
    onClose(selected)
  }, [onClose, selected])

  /** For root we use a dummy object */
  const handleSelectRoot = useCallback(() => {
    const sel = { id: 'ROOT', name: 'Top' }
    setSelected(sel as never)
  }, [])

  const handleDialogClick = (ev: MouseEvent) => {
    // We don't propagate clicks to the parent
    ev.stopPropagation()
  }

  return (
    <Dialog title={title} open={open} maxWidth="sm" fullWidth={true} onClick={handleDialogClick} onClose={handleCancel}>
      <DialogContent>
        {description ? <Typography variant="body2">{description}</Typography> : null}
        <EntityTree
          includeRoot={includeRoot}
          rootEntities={entities ?? allEntities}
          filter={filter}
          selected={selected}
          disableIds={excludeIds}
          disabledReason={disabledReason}
          onSelect={setSelected}
          onSelectRoot={handleSelectRoot}
        />
      </DialogContent>
      <DialogActions>
        {selected ? <Typography variant="subtitle1"> {selected.name}</Typography> : null}
        <Button disabled={!selected} onClick={handleOK}>
          {confirmLabel}
        </Button>
        <Button onClick={handleCancel}>Cancel</Button>
      </DialogActions>
    </Dialog>
  )
}
